/**
 * Domain Adapter - Single Source of Truth for Domain Configuration
 *
 * SINGLE-DOMAIN POLICY: Only supabank.xyz is allowed.
 * All w3are.com domains removed as part of brand consolidation.
 */

export type Surface = 'web' | 'admin' | 'mobile' | 'telegram';

/**
 * Environment-based domain configuration
 * Supports multiple domain aliases through environment variables
 */
const ENV_ALIASES = {
  // Production - ONLY supabank.xyz (single-root, no app. subdomain)
  APP_PUBLIC_ALIASES: process.env.APP_PUBLIC_ALIASES || 'https://supabank.xyz',
  ADMIN_PUBLIC_ALIASES: process.env.ADMIN_PUBLIC_ALIASES || 'https://admin.supabank.xyz',

  // Staging - ONLY supabank.xyz (subdomain-based surfaces per CANON)
  APP_STAGING_ALIASES: process.env.APP_STAGING_ALIASES || 'https://staging.supabank.xyz',
  ADMIN_STAGING_ALIASES: process.env.ADMIN_STAGING_ALIASES || 'https://admin.staging.supabank.xyz',

  // Local development
  APP_LOCAL_ALIASES: process.env.APP_LOCAL_ALIASES || 'http://localhost:8080,http://localhost:5173',
  ADMIN_LOCAL_ALIASES: process.env.ADMIN_LOCAL_ALIASES || 'http://localhost:8080/admin,http://localhost:5173/admin'
};

/**
 * Parse comma-separated aliases from environment variable
 */
function parseAliases(aliasString: string): string[] {
  return aliasString.split(',').map(url => url.trim()).filter(Boolean);
}

/**
 * Get all allowed origins for a specific surface
 */
export function getSurfaceOrigins(surface: Surface): string[] {
  switch (surface) {
    case 'web':
      return [
        ...parseAliases(ENV_ALIASES.APP_PUBLIC_ALIASES),
        ...parseAliases(ENV_ALIASES.APP_STAGING_ALIASES),
        ...parseAliases(ENV_ALIASES.APP_LOCAL_ALIASES)
      ];

    case 'admin':
      return [
        ...parseAliases(ENV_ALIASES.ADMIN_PUBLIC_ALIASES),
        ...parseAliases(ENV_ALIASES.ADMIN_STAGING_ALIASES),
        ...parseAliases(ENV_ALIASES.ADMIN_LOCAL_ALIASES)
      ];

    case 'mobile':
      // Mobile uses same origins as web + capacitor protocols
      return [
        ...getSurfaceOrigins('web'),
        'capacitor://localhost',
        'ionic://localhost'
      ];

    case 'telegram':
      // Telegram Mini App uses web origins + telegram protocols
      return [
        ...getSurfaceOrigins('web'),
        'https://web.telegram.org'
      ];


    default:
      return [];
  }
}

/**
 * Check if origin is allowed for specific surface
 *
 * @param origin - Origin to validate (e.g., 'https://supabank.xyz')
 * @param surface - Surface type ('web', 'admin', 'mobile', 'telegram')
 * @returns true if origin is allowed for this surface
 */
export function isAllowedOrigin(origin: string, surface: Surface): boolean {
  if (!origin) return false;

  // Normalize origin (remove trailing slashes)
  const normalizedOrigin = origin.replace(/\/$/, '');
  const allowedOrigins = getSurfaceOrigins(surface).map(o => o.replace(/\/$/, ''));

  return allowedOrigins.includes(normalizedOrigin);
}

/**
 * Get default origin for surface (first in priority list)
 */
export function getDefaultOrigin(surface: Surface): string {
  const origins = getSurfaceOrigins(surface);
  return origins[0] || '';
}

/**
 * Get primary production origin for surface
 * SINGLE-DOMAIN: Only supabank.xyz is returned
 */
export function getPrimaryOrigin(surface: Surface): string {
  switch (surface) {
    case 'web':
      return parseAliases(ENV_ALIASES.APP_PUBLIC_ALIASES)[0] || '';

    case 'admin':
      return parseAliases(ENV_ALIASES.ADMIN_PUBLIC_ALIASES)[0] || '';


    default:
      return getDefaultOrigin(surface);
  }
}

/**
 * Check if current environment is production
 */
export function isProductionEnv(): boolean {
  return process.env.NODE_ENV === 'production' ||
         process.env.VITE_RUNTIME_ENV === 'prod';
}

/**
 * Check if current environment is staging
 */
export function isStagingEnv(): boolean {
  return process.env.VITE_RUNTIME_ENV === 'staging';
}

/**
 * Check if current environment is development
 */
export function isDevelopmentEnv(): boolean {
  return process.env.NODE_ENV === 'development' ||
         process.env.VITE_RUNTIME_ENV === 'development' ||
         (!isProductionEnv() && !isStagingEnv());
}


/**
 * Domain Adapter configuration summary
 * Useful for debugging and monitoring
 */
export function getDomainAdapterConfig() {
  return {
    environment: {
      NODE_ENV: process.env.NODE_ENV,
      VITE_RUNTIME_ENV: process.env.VITE_RUNTIME_ENV,
      isProduction: isProductionEnv(),
      isStaging: isStagingEnv(),
      isDevelopment: isDevelopmentEnv()
    },
    surfaces: {
      web: {
        origins: getSurfaceOrigins('web'),
        default: getDefaultOrigin('web'),
        primary: getPrimaryOrigin('web')
      },
      admin: {
        origins: getSurfaceOrigins('admin'),
        default: getDefaultOrigin('admin'),
        primary: getPrimaryOrigin('admin')
      },
    },
    aliases: ENV_ALIASES
  };
}

/**
 * Backwards-compatible adapter wrapper
 * Provides the legacy getDomainAdapter contract expected by API consumers.
 */
export function getDomainAdapter() {
  return {
    isAllowedOrigin,
    getDefaultOrigin,
    getSurfaceOrigins,
    getPrimaryOrigin,
    getDomainAdapterConfig
  };
}